import type { ReactNode } from 'react';
import type { ButtonProps } from '../../Button/Button';
import type { LinkButtonProps } from '../../LinkButton/LinkButton';

import { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import Button from '../../Button/Button';
import LinkButton from '../../LinkButton/LinkButton';
import Tooltip from '../../Tooltip/Tooltip';

const colorClasses = {
  black:
    'text-default hover:text-default active:text-default focus:text-default',
  blue: 'text-blue hover:text-blue active:text-blue focus:text-blue',
  red: 'text-red-600 hover:text-red-600 dark:text-red',
  green: 'text-lime-700 hover:text-lime-700 dark:text-lime-500',
  orange: 'text-orange-500 hover:text-orange-700 dark:text-orange-700',
  purple: 'text-indigo-700 hover:text-indigo-700 dark:text-indigo-400',
};

export const baseUIPopoverItemClasses =
  'h-9 xl:h-10 px-6 rounded-none w-full justify-end text-base 2xl:text-base' +
  ' hover:underline hover:bg-gradient-menu-active dark:hover:bg-gradient-dark-menu-active';

export type PopoverColor = keyof typeof colorClasses;

export interface PopoverItemBaseProps {
  /**
   * Popover icon
   */
  icon?: ReactNode;
  /**
   * Popover button text color
   */
  color?: PopoverColor;
  /**
   * Button label
   */
  label?: ReactNode;
  /**
   * If button is disabled
   */
  disabled?: boolean;
  /**
   * Button tooltip text
   */
  tooltip?: string;
  /**
   * Additional popover button classes
   */
  additionalClasses?: string;
  /**
   * Additional popover button icon classes
   */
  additionalIconClasses?: string;
}

export type PopoverItemProps =
  | (PopoverItemBaseProps &
      Omit<LinkButtonProps, keyof PopoverItemBaseProps | 'as'> & {
        /**
         * Item link
         */
        link: string;
      })
  | (PopoverItemBaseProps &
      Omit<ButtonProps, keyof PopoverItemBaseProps | 'as'> & {
        /**
         * Item link
         */
        link?: never;
      });

const PopoverItem = ({
  icon,
  color = 'black',
  link,
  label = '',
  disabled = false,
  additionalClasses = '',
  additionalIconClasses = '',
  tooltip,
  ...props
}: PopoverItemProps) => {
  const baseProps = useMemo(
    () => ({
      buttonColor: 'borderless' as const,
      buttonSize: 'base' as const,
      iconPosition: 'start' as const,
      iconImage: icon,
      disabled,
      additionalClasses: twMerge(
        baseUIPopoverItemClasses,
        !disabled && 'cursor-pointer',
        colorClasses[color] || colorClasses.black,
        additionalClasses,
      ),
      additionalIconClasses: twMerge('mr-2', additionalIconClasses),
    }),
    [icon, disabled, color, additionalClasses, additionalIconClasses],
  );

  return (
    <Tooltip
      tooltip={tooltip}
      tooltipPlacement="leftCenter"
      phoneTooltipPlacement="leftCenter"
    >
      {link ? (
        <LinkButton
          {...baseProps}
          {...(props as Omit<LinkButtonProps, keyof PopoverItemBaseProps>)}
          link={link}
        >
          {label}
        </LinkButton>
      ) : (
        <Button
          {...baseProps}
          {...(props as Omit<ButtonProps, keyof PopoverItemBaseProps>)}
        >
          {label}
        </Button>
      )}
    </Tooltip>
  );
};

export default PopoverItem;
